import React, { Component, Fragment } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import * as totemReportsActions from 'action/totemReportsNew'
import { withTotemTokenRetry } from 'action/auth'
import PropTypes from 'prop-types'
import ContentHeader from 'component/utils/ContentHeader'
import Button from 'component/Button/index'
import { SideFilterForm } from 'component/totemReportsNew/SideFilter'
import Chart from 'component/totemReportsNew/Chart'
import OrdersModal from './OrdersModal'
import Form from 'component/commonReport/Form'
import './styles.css'
import FilterIconSVG from 'component/svgComponents/filter'
import { DetailsModal } from 'component/totemReportsNew/DetailsModal'
import { FORMAT_HOUR } from 'constants/config'
import moment from 'moment'
import _ from 'lodash'
import memoizeOne from 'memoize-one'
import ErrorModal from 'component/Modal/ErrorModal'
import DownloadIconSVG from 'component/svgComponents/download'

let signalController;
const TIME_LIMIT = moment('00:05:00', FORMAT_HOUR)

const DEFAULT_MODAL_STATE = {
  title: '',
  header: [],
  show: false,
  colorizeTime: true,
  data: [],
  showReasons: false,
  queueReportType: '',
  extraFilter: undefined,
}

const DEFAULT_DETAILS_STATE = {
  orderDetails: {},
  show: false,
}

const sortByDateTime = (items) =>
  items
    .slice()
    .sort((a, b) => {
      const aMoment = moment(
        `${a.date} ${a.dateHour}`,
        `DD/MM/YYYY ${FORMAT_HOUR}`,
        true
      )
      const bMoment = moment(
        `${b.date} ${b.dateHour}`,
        `DD/MM/YYYY ${FORMAT_HOUR}`,
        true
      )

      if (!aMoment.isValid() || !bMoment.isValid()) return 0
      return aMoment.diff(bMoment)
    })

const REPORT_TYPES = {
  GENERAL: 'GENERAL',
  ORDERS_BY_STORE: 'ORDERS_BY_STORE',
  ORDERS_BY_DAY: 'ORDERS_BY_DAY',
  AVERAGE_TIME_PER_DAY: 'AVERAGE_TIME_PER_DAY',
  REASONS_FOR_DELETING_MONITOR_ORDERS: 'REASONS_FOR_DELETING_MONITOR_ORDERS',
  QUANTITY_OF_ORDER: 'QUANTITY_OF_ORDER',
  AVERAGE_TIME: 'AVERAGE_TIME',
  ORDERS_UNDER_10_MIN: 'ORDERS_UNDER_10_MIN',
  ORDERS_OVER_10_MIN: 'ORDERS_OVER_10MIN'
}


const calculateMinutes = (averageTime) => {
  const inMoment = moment(averageTime, FORMAT_HOUR)

  const totalMinutes = inMoment.minutes() + 60 * inMoment.hours()
  return totalMinutes < 10
    ? moment(`${totalMinutes}:${inMoment.seconds()}`, 'mm:ss').format('mm:ss')
    : `${totalMinutes}:${moment(inMoment.seconds(), 'ss').format('ss')}`
}

const calculateValuesY = (data, insertFive) => {
  const i = Math.max(...data) + 15 - (Math.max(...data) % 15)

  if (insertFive) {
    const valuesY = [0, 1, 2, 3].map((multiplicator) => (i / 3) * multiplicator)

    if (!valuesY.find((value) => value === 5) && Math.max(...valuesY) <= 60) {
      const index = valuesY.indexOf(valuesY.find((value) => value > 5))
      valuesY.splice(index, 1, 5)
    }

    return valuesY
  }

  return [0, 1, 2, 3].map((multiplicator) => (i / 3) * multiplicator)
}

const wrapLabel = (words, maxLength) => {
  const mountLine = (current = '', add) => {
    current += add
    current += ' '
    return current
  }

  const { lines } = words.reduce(
    ({ lines, currentIndex }, current) => {
      if (`${lines[currentIndex] || ''} `.length + current.length < maxLength) {
        lines[currentIndex] = mountLine(lines[currentIndex], current)
        return { currentIndex, lines }
      }

      currentIndex += 1
      lines[currentIndex] = mountLine(lines[currentIndex], current)
      return { lines, currentIndex }
    },
    { currentIndex: 0, lines: [] }
  )

  return lines
}

const catchTime = (value) => {
  switch (value) {
    case 1:
      return { time: ['above', 'below'], justExcluded: false }
    case 2:
      return { time: ['above', 'below'], justExcluded: true }
    case 3:
      return { time: ['below'], justExcluded: false }
    case 4:
      return { time: ['above'], justExcluded: false }
    default:
      return { time: ['above', 'below'], justExcluded: false }
  }
}

const catchQuantity = (value) => {
  switch (value) {
    case 1:
      return ['limitUpTo', 'limitAbove']
    case 2:
      return ['limitAbove']
    case 3:
      return ['limitUpTo']
    default:
      return ['limitUpTo', 'limitAbove']
  }
}

const catchWeight = (value) => {
  switch (value) {
    case 1:
      return ['above', 'below']
    case 2:
      return ['below']
    case 3:
      return ['above']
    default:
      return ['above', 'below']
  }
}

class TotemReportsContainer extends Component {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    error: PropTypes.object,
    reports: PropTypes.array,
    translate: PropTypes.func.isRequired,
    fetchTotemReports: PropTypes.func.isRequired,
    fetchTotemReportsFlux: PropTypes.func.isRequired,
    fetchTotemReportsCancel: PropTypes.func.isRequired,
    isFetching: PropTypes.bool,
    fetchTotemReportsCsvFlux: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
  }

  constructor() {
    super()

    this.state = {
      showFilter: true,
      showSideFilter: false,
      showErrorModal: false,
      sideFilterValues: {},
      ordersModal: DEFAULT_MODAL_STATE,
      detailsModal: DEFAULT_DETAILS_STATE,
      reportType: REPORT_TYPES,
    }

    this.getDataChart1 = memoizeOne((reports) => {
      const accumulator = reports.reduce((acc, current) => {
        const type = current.excluded
          ? 'excluded'
          : moment(current.waitingTime, FORMAT_HOUR) > TIME_LIMIT
            ? 'above'
            : 'below'

        const DEFAULT = { store: current.store, excluded: 0, above: 0, below: 0 }
        DEFAULT[type]++

        if (!acc.some((number) => number.store === current.store))
          acc.push(DEFAULT)
        else acc.find((number) => number.store === current.store)[type]++
        return acc
      }, [])

      const getStoreCode = (item) => {
        const storeLabel = item.store || ''
        return storeLabel.indexOf(' - ') >= 0
          ? storeLabel.split(' - ')[0].trim()
          : storeLabel
      }

      return accumulator.sort((a, b) => {
        const codeA = getStoreCode(a)
        const codeB = getStoreCode(b)
        const numA = parseInt(codeA, 10)
        const numB = parseInt(codeB, 10)
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
        return String(codeA).localeCompare(String(codeB))
      })
    })

    this.getDataChart2 = memoizeOne((reports) => reports
      .reduce((accumulator, current) => {
        const DEFAULT = { date: current.date, purchases: 1 }

        if (!accumulator.some((purchase) => purchase.date === current.date))
          accumulator.push(DEFAULT)
        else
          accumulator.find((purchase) => purchase.date === current.date)
            .purchases++
        return accumulator
      }, [])
      .reverse())

    this.getDataChart3 = memoizeOne((reports) => {
      const getTimeAverage = (orders) => {
        const sumTime =
          orders
            .map(({ waitingTime }) => waitingTime)
            .reduce(
              (accumulator, current) =>
                accumulator
                  .add(current.slice(0, 2), 'hours')
                  .add(current.slice(3, 5), 'minutes')
                  .add(current.slice(6, 8), 'seconds'),
              moment().startOf('day')
            ) - moment().startOf('day')

        return moment()
          .startOf('day')
          .milliseconds(sumTime / orders.length)
          .format('HH:mm:ss')
      }

      const averageByDay = Object.entries(_.groupBy(reports, 'date')).reduce(
        (accumulator, [currentDate, currentOrders]) => {
          accumulator[currentDate] = getTimeAverage(currentOrders)
          return accumulator
        },
        {}
      )

      return Object.keys(averageByDay)
        .map((date) => {
          const [minutes] = calculateMinutes(averageByDay[date]).split(':')

          return {
            date,
            average: minutes,
            seconds: moment(averageByDay[date], FORMAT_HOUR).seconds(),
          }
        })
        .reverse()
    })

    this.getDataChart4 = memoizeOne((reports) => reports
      .reduce((accumulator, current) => {
        const DEFAULT = {
          exclusionReason: current.exclusionReason,
          purchases: 1,
        }

        if (
          current.excluded &&
          !accumulator.some(
            (purchase) => purchase.exclusionReason === current.exclusionReason
          )
        )
          accumulator.push(DEFAULT)
        else if (current.excluded)
          accumulator.find(
            (purchase) => purchase.exclusionReason === current.exclusionReason
          ).purchases++
        return accumulator
      }, [])
      .reverse())

    this.getReportStats = memoizeOne((reports) => {
      if (!reports.length) {
        return {
          orderQuantity: 0,
          averageTime: '00:00:00',
          orderBelow: 0,
          orderAbove: 0,
        }
      }

      const orderQuantity = reports.length

      const averageTime = moment()
        .startOf('day')
        .milliseconds(
          (reports.reduce((accumulator, { waitingTime: current }) => {
            const [hours, minutes, seconds] = current.split(':')
            return accumulator
              .add(hours, 'hours')
              .add(minutes, 'minutes')
              .add(seconds, 'seconds')
          }, moment().startOf('day')) - moment().startOf('day')) / reports.length
        )
        .format(FORMAT_HOUR)

      const orderBelow = reports.filter(
        (report) => moment(report.waitingTime, FORMAT_HOUR) <= TIME_LIMIT
      ).length

      const orderAbove = reports.filter(
        (report) => moment(report.waitingTime, FORMAT_HOUR) > TIME_LIMIT
      ).length

      return { orderQuantity, averageTime, orderBelow, orderAbove }
    })

    this.handleFilter = this.handleFilter.bind(this)
    this.handleShowSideFilter = this.handleShowSideFilter.bind(this)
    this.handleHideSideFilter = this.handleHideSideFilter.bind(this)
    this.handleQuantityWidgetClick = this.handleQuantityWidgetClick.bind(this)
    this.handleTimeOptionsWidgetClick = this.handleTimeOptionsWidgetClick.bind(this)
    this.handleBelowWidgetClick = this.handleBelowWidgetClick.bind(this)
    this.handleAboveWidgetClick = this.handleAboveWidgetClick.bind(this)
    this.handleCloseOrdersModal = this.handleCloseOrdersModal.bind(this)
    this.handleShowDetailModal = this.handleShowDetailModal.bind(this)
    this.handleCloseDetailsModal = this.handleCloseDetailsModal.bind(this)
    this.handleTurnBackFromDetailsModal = this.handleTurnBackFromDetailsModal.bind(this)
    this.renderModalError = this.renderModalError.bind(this)
    this.handleDownloadGeneral = this.handleDownloadGeneral.bind(this)
    this.handleDownloadOrdersByStore = this.handleDownloadOrdersByStore.bind(this)
    this.handleDownloadOrdersByDay = this.handleDownloadOrdersByDay.bind(this)
    this.handleDownloadAverageTime = this.handleDownloadAverageTime.bind(this)
    this.handleDownloadReasons = this.handleDownloadReasons.bind(this)
    this.renderChart1Bar = this.renderChart1Bar.bind(this)
    this.renderChart1AxisBottom = this.renderChart1AxisBottom.bind(this)
    this.renderChartAxisLeft = this.renderChartAxisLeft.bind(this)
    this.renderChart2Bar = this.renderChart2Bar.bind(this)
    this.renderChart2AxisBottom = this.renderChart2AxisBottom.bind(this)
    this.renderChart3Bar = this.renderChart3Bar.bind(this)
    this.renderChart3AxisBottom = this.renderChart3AxisBottom.bind(this)
    this.renderChart3AxisLeft = this.renderChart3AxisLeft.bind(this)
    this.renderChart4Bar = this.renderChart4Bar.bind(this)
    this.renderChart4AxisBottom = this.renderChart4AxisBottom.bind(this)
  }

  renderError() {
    this.setState({ showErrorModal: true })
  }

  cleanRenderError() {
    this.setState({ showErrorModal: false })
  }

  componentDidUpdate(prevProps) {
    const { error } = this.props

    if (error && prevProps.error !== error) this.renderError()
    else if (!error && prevProps.error !== error) this.cleanRenderError()
  }

  componentWillUnmount() {
    if (signalController) {
      signalController.abort()
    }
  }


  headers(below, above, excluded) {
    const { translate } = this.props

    const complement = (value) =>
      value - 1
        ? translate('totem.reports.modal.table.header.orders').toLowerCase()
        : translate('totem.reports.modal.order.detail.order').toLowerCase()

    const returnArray = []

    if (below >= 0) {
      returnArray.push(
        <div
          style={{
            width: '100%',
            border: '1px solid #41A017',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 8,
            height: 70,
            marginRight: 10,
            justifyContent: 'space-evenly',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 18,
            }}
          >
            {translate('totem.reports.modal.card.neg.label')}
          </span>
          <span
            style={{
              fontSize: 24,
              color: '#41A017',
            }}
          >
            <strong>{below}</strong> {complement(below)}
          </span>
        </div>
      )
    }

    if (above >= 0) {
      returnArray.push(
        <div
          style={{
            width: '100%',
            border: '1px solid #B42A27',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 8,
            height: 70,
            justifyContent: 'space-evenly',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 18,
            }}
          >
            {translate('totem.reports.modal.card.pos.label')}
          </span>
          <span
            style={{
              fontSize: 24,
              color: '#B42A27',
            }}
          >
            <strong>{above}</strong> {complement(above)}
          </span>
        </div>
      )
    }

    if (excluded >= 0) {
      returnArray.push(
        <div
          style={{
            width: '100%',
            border: '1px solid #C86F21',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 8,
            height: 70,
            marginLeft: 10,
            justifyContent: 'space-evenly',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 18,
            }}
          >
            {translate('totem.reports.modal.excluded')}
          </span>
          <span
            style={{
              fontSize: 24,
              color: '#C86F21',
            }}
          >
            <strong>{excluded}</strong> {complement(excluded)}
          </span>
        </div>
      )
    }

    return returnArray
  }

  get indicators() {
    const { translate } = this.props

    return [
      {
        color: 'rgba(0, 117, 116, 0.8)',
        description: translate('totem.reports.time.options.under'),
      },
      {
        color: 'rgba(223, 56, 40, 0.8)',
        description: translate('totem.reports.time.options.above'),
      },
      {
        color: 'rgba(252, 150, 30, 0.8)',
        description: translate('totem.query.log.sales.order.deleted.option'),
      },
      { color: 'rgba(6, 58, 68, 0.8)', description: '' },
    ]
  }

  get formFields() {
    const { translate } = this.props

    return [
      {
        type: 'branchs',
        name: 'idBranches',
        label: translate('field.selectStore'),
        help_text: translate('totalAccuracy.store'),
        required: true,
      },
      {
        type: 'date_range',
        name: 'dtPeriod',
        label: translate('field.period.hint'),
        help_text: translate('totalAccuracy.dtYear.filter'),
        required: true,
      },
    ]
  }

  handleFilter({ idBranches, dtPeriod, time, weight, quantity, reportType }) {
    const {
      auth: { user },
      dispatch,
    } = this.props

    const formattedDtPeriod = Array.isArray(dtPeriod)
      ? dtPeriod.map((dt) => moment(dt).format('DD/MM/YYYY'))
      : [];

    const data = {
      idBranches,
      dtPeriod: formattedDtPeriod,
      weight: catchWeight(weight),
      quantity: catchQuantity(quantity),
      queueReportType: reportType,
      ...catchTime(time),
    }

    dispatch(
      withTotemTokenRetry((totemToken) => {
        signalController = new AbortController()
        return totemReportsActions.fetchTotemReportsFlux(
          user.dsToken,
          totemToken,
          data,
          signalController
        )
      })
    )
      .then(() => {
        this.setState({
          showFilter: false,
          showSideFilter: false,
          sideFilterValues: {
            idBranches,
            dtPeriod,
            time: time || 1,
            weight: weight || 1,
            quantity: quantity || 1,
          },
        })
      })
      .catch(() => {
        this.setState({ showErrorModal: true });
      })
  }

  renderModalError() {
    const { translate } = this.props

    const handleCloseModal = () => {
      this.setState({ showErrorModal: false });
    };

    return (
      <ErrorModal
        size='sm'
        handleClose={handleCloseModal}
        msg={translate('application.fatal.error')}
        t={translate}
        center
        buttonText={translate('ok_button')}
      />
    )
  }


  catchReportsByStore(store) {
    const { reports } = this.props

    return reports
      .filter((report) => report.store === store)
      .slice()
      .sort((a, b) => {
        const aMoment = moment(
          `${a.date} ${a.dateHour}`,
          `DD/MM/YYYY ${FORMAT_HOUR}`,
          true
        )
        const bMoment = moment(
          `${b.date} ${b.dateHour}`,
          `DD/MM/YYYY ${FORMAT_HOUR}`,
          true
        )

        if (!aMoment.isValid() || !bMoment.isValid()) return 0
        return aMoment.diff(bMoment)
      })
  }

  catchReportsByDate(date) {
    const { reports } = this.props

    return reports
      .filter((report) => report.date === date)
      .slice()
      .sort((a, b) => {
        const aMoment = moment(
          `${a.date} ${a.dateHour}`,
          `DD/MM/YYYY ${FORMAT_HOUR}`,
          true
        )
        const bMoment = moment(
          `${b.date} ${b.dateHour}`,
          `DD/MM/YYYY ${FORMAT_HOUR}`,
          true
        )

        if (!aMoment.isValid() || !bMoment.isValid()) return 0
        return aMoment.diff(bMoment)
      })
  }

  catchReportsByExclusionReason(exclusionReason) {
    const { reports } = this.props

    return reports
      .filter(
        (report) =>
          report.excluded && report.exclusionReason === exclusionReason
      )
      .slice()
      .sort((a, b) => {
        const aMoment = moment(
          `${a.date} ${a.dateHour}`,
          `DD/MM/YYYY ${FORMAT_HOUR}`,
          true
        )
        const bMoment = moment(
          `${b.date} ${b.dateHour}`,
          `DD/MM/YYYY ${FORMAT_HOUR}`,
          true
        )

        if (!aMoment.isValid() || !bMoment.isValid()) return 0
        return aMoment.diff(bMoment)
      })
  }


  handleDownloadReport = (idBranches, dtPeriod, time, weight, quantity, reportType, extraFilter) => {
    const {
      dispatch,
    } = this.props

    const formattedDtPeriod = Array.isArray(dtPeriod)
      ? dtPeriod.map((dt) => moment(dt).format('DD/MM/YYYY'))
      : [];

    const data = {
      idBranches,
      dtPeriod: formattedDtPeriod,
      weight: catchWeight(weight),
      quantity: catchQuantity(quantity),
      queueReportType: reportType,
      ...catchTime(time),
      ...(extraFilter && Object.keys(extraFilter).length > 0 ? extraFilter : {}),
    }

    this.setState((prevState) => ({
      ordersModal: {
        ...prevState.ordersModal,
        show: false,
      }
    }));

    dispatch(
      withTotemTokenRetry((totemToken) => {
        signalController = new AbortController()
        return totemReportsActions.fetchTotemReportsCsvFlux(
          totemToken,
          data,
          signalController
        )
      })
    )
      .then((response) => {
        if (Object.keys(response).length === 0) {
          this.setState({ showErrorModal: true });
        }
      })
      .catch(() => {
        this.setState({ showErrorModal: true });
      });
  };

  getDownloadHandler = (reportType, useExtraFilter = false) => () => {
    const { sideFilterValues, ordersModal } = this.state;

    this.handleDownloadReport(
      sideFilterValues.idBranches,
      sideFilterValues.dtPeriod,
      sideFilterValues.time,
      sideFilterValues.weight,
      sideFilterValues.quantity,
      reportType,
      useExtraFilter ? ordersModal?.extraFilter : undefined
    );
  };

  handleDownloadGeneral() {
    this.getDownloadHandler(REPORT_TYPES.GENERAL)()
  }

  handleDownloadOrdersByStore() {
    this.getDownloadHandler(REPORT_TYPES.ORDERS_BY_STORE)()
  }

  handleDownloadOrdersByDay() {
    this.getDownloadHandler(REPORT_TYPES.ORDERS_BY_DAY)()
  }

  handleDownloadAverageTime() {
    this.getDownloadHandler(REPORT_TYPES.AVERAGE_TIME_PER_DAY)()
  }

  handleDownloadReasons() {
    this.getDownloadHandler(REPORT_TYPES.REASONS_FOR_DELETING_MONITOR_ORDERS)()
  }

  handleShowSideFilter() {
    this.setState({ showSideFilter: true })
  }

  handleHideSideFilter() {
    this.setState({ showSideFilter: false })
  }

  handleQuantityWidgetClick() {
    const { translate, reports } = this.props

    this.setState((prevState) => ({
      ordersModal: {
        ...prevState.ordersModal,
        title: translate('totem.reports.filter.header.quantity'),
        queueReportType: "QUANTITY_OF_ORDER",
        extraFilter: undefined,
        header: [],
        show: true,
        colorizeTime: true,
        showReasons: false,
        data: sortByDateTime(reports).map((report) => ({
          ...report,
          waitingTime: moment(
            report.waitingTime,
            FORMAT_HOUR
          ).format(FORMAT_HOUR),
        })),
      },
    }))
  }

  handleTimeOptionsWidgetClick() {
    const { translate, reports } = this.props

    this.setState((prevState) => ({
      ordersModal: {
        ...prevState.ordersModal,
        title: translate('totem.reports.time.options.title'),
        queueReportType: "AVERAGE_TIME",
        extraFilter: undefined,
        header: [],
        show: true,
        colorizeTime: true,
        showReasons: false,
        data: sortByDateTime(reports).map((report) => ({
          ...report,
          waitingTime: moment(
            report.waitingTime,
            FORMAT_HOUR
          ).format(FORMAT_HOUR),
        })),
      },
    }))
  }

  handleBelowWidgetClick() {
    const { translate, reports } = this.props

    this.setState({
      ordersModal: {
        title: translate('totem.reports.modal.title.below'),
        queueReportType: "ORDERS_UNDER_10_MIN",
        extraFilter: undefined,
        header: [],
        show: true,
        colorizeTime: false,
        showReasons: false,
        data: sortByDateTime(
          reports.filter(
            (report) =>
              moment(report.waitingTime, FORMAT_HOUR) <=
              TIME_LIMIT
          )
        ).map((report) => ({
            ...report,
            waitingTime: moment(
              report.waitingTime,
              FORMAT_HOUR
            ).format(FORMAT_HOUR),
          })),
      },
    })
  }

  handleAboveWidgetClick() {
    const { translate, reports } = this.props

    this.setState({
      ordersModal: {
        title: translate('totem.reports.modal.title.above'),
        queueReportType: "ORDERS_OVER_10_MIN",
        extraFilter: undefined,
        header: [],
        show: true,
        colorizeTime: false,
        showReasons: false,
        data: sortByDateTime(
          reports.filter(
            (report) =>
              moment(report.waitingTime, FORMAT_HOUR) >
              TIME_LIMIT
          )
        ).map((report) => ({
            ...report,
            waitingTime: moment(
              report.waitingTime,
              FORMAT_HOUR
            ).format(FORMAT_HOUR),
          })),
      },
    })
  }

  handleCloseOrdersModal() {
    this.setState({ ordersModal: DEFAULT_MODAL_STATE })
  }

  handleShowDetailModal(order) {
    this.setState(
      (prevState) => ({ ordersModal: { ...prevState.ordersModal, show: false } }),
      () => {
        this.setState({
          detailsModal: {
            orderDetails: order,
            show: true,
          },
        })
      }
    )
  }

  handleCloseDetailsModal() {
    this.setState({
      ordersModal: DEFAULT_MODAL_STATE,
      detailsModal: DEFAULT_DETAILS_STATE,
    })
  }

  handleTurnBackFromDetailsModal() {
    this.setState((prevState) => ({
      ordersModal: { ...prevState.ordersModal, show: true },
      detailsModal: DEFAULT_DETAILS_STATE,
    }))
  }

  renderChart1Bar(barProps, renderLateralIndicator, closeLateralIndicator) {
    const { reports, translate } = this.props
    const { label: value, bar: { x, y, height, color, data: barData }, index: barIndex } = barProps
    const data = this.getDataChart1(reports)
    let barIndexResolved

    if (barData && barData.indexValue != null) {
      const idx = data.findIndex((d) => d.store === barData.indexValue)
      if (idx >= 0) barIndexResolved = idx
    }
    if (barIndexResolved === undefined && data.length > 0 && barIndex !== undefined) {
      barIndexResolved = barIndex % data.length
    }
    if (barIndexResolved === undefined && typeof x === 'number') {
      barIndexResolved = Math.round((x - 15) / (28 + 35))
    }
    if (barIndexResolved === undefined || barIndexResolved < 0) barIndexResolved = 0
    const element = data[barIndexResolved]
    if (!element) return null
    const storeLabel = element.store
    const storeId = storeLabel.indexOf(' - ') >= 0 ? storeLabel.split(' - ')[0].trim() : storeLabel

    return (
      <rect
        cursor='pointer'
        fill={color}
        width={28}
        height={height}
        x={15 + (28 + 35) * barIndexResolved}
        y={y}
        onClick={() => {
          this.setState((prevState) => ({
            ordersModal: {
              ...prevState.ordersModal,
              title: translate(
                'totem.reports.filter.header.store'
              ),
              queueReportType: REPORT_TYPES.ORDERS_BY_STORE,
              extraFilter: { store: storeId },
              header: this.headers(
                element.below,
                element.above,
                element.excluded
              ),
              show: true,
              colorizeTime: true,
              showReasons: false,
              data: this.catchReportsByStore(storeLabel),
            },
          }))
          closeLateralIndicator()
        }}
        onMouseEnter={(e) =>
          renderLateralIndicator(e, value, barIndexResolved)
        }
        onMouseLeave={closeLateralIndicator}
      />
    )
  }

  renderChart1AxisBottom(e) {
    const { reports, translate } = this.props
    const [id, ...rest] = e.value.split(' - ')
    const storeLabel = e.value
    const store = rest.join(' - ')
    const data = this.getDataChart1(reports)
    const element = data[e.tickIndex]
    const storeId = storeLabel.indexOf(' - ') >= 0 ? storeLabel.split(' - ')[0].trim() : storeLabel

    const openStoreModal = () => {
      if (!element) return
      this.setState((prevState) => ({
        ordersModal: {
          ...prevState.ordersModal,
          title: translate('totem.reports.filter.header.store'),
          queueReportType: REPORT_TYPES.ORDERS_BY_STORE,
          extraFilter: { store: storeId },
          header: this.headers(element.below, element.above, element.excluded),
          show: true,
          colorizeTime: true,
          showReasons: false,
          data: this.catchReportsByStore(storeLabel),
        },
      }))
    }

    return (
      <g
        style={{ cursor: 'pointer' }}
        onClick={openStoreModal}
      >
        <text
          alignmentBaseline={e.textBaseline}
          textAnchor={e.textAnchor}
        >
          <tspan
            x={29 + 63 * e.tickIndex}
            y={15}
            style={{
              fontSize: 10,
            }}
          >
            {id}
          </tspan>
          {wrapLabel(store.split(' '), 12).map((line, i) => (
            <tspan
              key={`${line}-${i}`}
              x={29 + 63 * e.tickIndex}
              y={15 + 10 * (i + 1)}
              style={{
                fontSize: 10,
              }}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    )
  }

  renderChartAxisLeft(e) {
    return (
      <text
        alignmentBaseline={e.textBaseline}
        textAnchor={e.textAnchor}
      >
        <tspan
          x={e.x - 10}
          y={e.y + 3}
          style={{
            fontSize: 10,
          }}
        >
          {e.value}
        </tspan>
      </text>
    )
  }

  renderChart2Bar({ label: value, bar: { y, height, color, label } }, renderLateralIndicator, closeLateralIndicator) {
    const { reports, translate } = this.props
    const [, date] = label.split(' - ')
    const data = this.getDataChart2(reports)
    const index = data.indexOf(
      data.find((chart) => chart.date === date)
    )

    return (
      <rect
        cursor='pointer'
        fill={color}
        width={28}
        height={height}
        x={15 + (28 + 20) * index}
        y={y}
        onClick={() => {
          this.setState((prevState) => ({
            ordersModal: {
              ...prevState.ordersModal,
              title: translate(
                'totem.reports.graph.quantity.title'
              ),
              queueReportType: REPORT_TYPES.ORDERS_BY_DAY,
              extraFilter: { date },
              header: this.headers(
                this.catchReportsByDate(date).filter(
                  (report) =>
                    moment(report.waitingTime, FORMAT_HOUR) <=
                    TIME_LIMIT
                ).length,
                this.catchReportsByDate(date).filter(
                  (report) =>
                    moment(report.waitingTime, FORMAT_HOUR) >
                    TIME_LIMIT
                ).length
              ),
              show: true,
              colorizeTime: true,
              showReasons: false,
              data: this.catchReportsByDate(date),
            },
          }))
          closeLateralIndicator()
        }}
        onMouseEnter={(e) =>
          renderLateralIndicator(e, value, index)
        }
        onMouseLeave={closeLateralIndicator}
      />
    )
  }

  renderChart2AxisBottom(e) {
    const { translate } = this.props
    const [day, month, year] = e.value.split('/')
    const date = e.value

    const openDateModal = () => {
      this.setState((prevState) => ({
        ordersModal: {
          ...prevState.ordersModal,
          title: translate('totem.reports.graph.quantity.title'),
          queueReportType: REPORT_TYPES.ORDERS_BY_DAY,
          extraFilter: { date },
          header: this.headers(
            this.catchReportsByDate(date).filter(
              (report) =>
                moment(report.waitingTime, FORMAT_HOUR) <=
                TIME_LIMIT
            ).length,
            this.catchReportsByDate(date).filter(
              (report) =>
                moment(report.waitingTime, FORMAT_HOUR) >
                TIME_LIMIT
            ).length
          ),
          show: true,
          colorizeTime: true,
          showReasons: false,
          data: this.catchReportsByDate(date),
        },
      }))
    }

    return (
      <g
        style={{ cursor: 'pointer' }}
        onClick={openDateModal}
      >
        <text
          alignmentBaseline={e.textBaseline}
          textAnchor={e.textAnchor}
        >
          <tspan
            x={29 + 48 * e.tickIndex}
            y={15}
            style={{
              fontSize: 10,
            }}
          >
            {[day, month].join('/')}
          </tspan>
          <tspan
            x={29 + 48 * e.tickIndex}
            y={25}
            style={{
              fontSize: 10,
            }}
          >
            {year}
          </tspan>
        </text>
      </g>
    )
  }

  renderChart3Bar({ label: value, bar: { y, height, label } }, renderLateralIndicator, closeLateralIndicator) {
    const { reports, translate } = this.props
    const [below, above] = this.indicators
    const [, date] = label.split(' - ')
    const data = this.getDataChart3(reports)
    const index = data.indexOf(
      data.find((chart) => chart.date === date)
    )

    return (
      <rect
        cursor='pointer'
        fill={
          value >= 5 ||
            (value === 5 && data[index].seconds > 0)
            ? above.color
            : below.color
        }
        width={28}
        height={height}
        x={15 + (28 + 35) * index}
        y={y}
        onClick={() => {
          this.setState((prevState) => ({
            ordersModal: {
              ...prevState.ordersModal,
              title: translate(
                'totem.reports.graph.time.title'
              ),
              queueReportType: REPORT_TYPES.AVERAGE_TIME_PER_DAY,
              extraFilter: { date },
              header: this.headers(
                this.catchReportsByDate(date).filter(
                  (report) =>
                    moment(report.waitingTime, FORMAT_HOUR) <=
                    TIME_LIMIT
                ).length,
                this.catchReportsByDate(date).filter(
                  (report) =>
                    moment(report.waitingTime, FORMAT_HOUR) >
                    TIME_LIMIT
                ).length
              ),
              show: true,
              colorizeTime: true,
              showReasons: false,
              data: this.catchReportsByDate(date),
            },
          }))
          closeLateralIndicator()
        }}
        onMouseEnter={(e) =>
          renderLateralIndicator(
            e,
            this.catchReportsByDate(date).length,
            index
          )
        }
        onMouseLeave={closeLateralIndicator}
      />
    )
  }

  renderChart3AxisBottom(e) {
    const { translate } = this.props
    const [day, month, year] = e.value.split('/')
    const date = e.value

    const openDateModal = () => {
      this.setState((prevState) => ({
        ordersModal: {
          ...prevState.ordersModal,
          title: translate(
            'totem.reports.graph.time.title'
          ),
          queueReportType: REPORT_TYPES.AVERAGE_TIME_PER_DAY,
          extraFilter: { date },
          header: this.headers(
            this.catchReportsByDate(date).filter(
              (report) =>
                moment(report.waitingTime, FORMAT_HOUR) <=
                TIME_LIMIT
            ).length,
            this.catchReportsByDate(date).filter(
              (report) =>
                moment(report.waitingTime, FORMAT_HOUR) >
                TIME_LIMIT
            ).length
          ),
          show: true,
          colorizeTime: true,
          showReasons: false,
          data: this.catchReportsByDate(date),
        },
      }))
    }

    return (
      <g
        style={{ cursor: 'pointer' }}
        onClick={openDateModal}
      >
        <text
          alignmentBaseline={e.textBaseline}
          textAnchor={e.textAnchor}
        >
          <tspan
            x={29 + 63 * e.tickIndex}
            y={15}
            style={{
              fontSize: 10,
            }}
          >
            {[day, month].join('/')}
          </tspan>
          <tspan
            x={29 + 63 * e.tickIndex}
            y={25}
            style={{
              fontSize: 10,
            }}
          >
            {year}
          </tspan>
        </text>
      </g>
    )
  }

  renderChart3AxisLeft(e) {
    return (
      <text
        alignmentBaseline={e.textBaseline}
        textAnchor={e.textAnchor}
      >
        <tspan
          x={e.x - 10}
          y={e.y + 3}
          style={{
            fontSize: 10,
          }}
        >
          {e.value}
        </tspan>
        {e.value > 0 && (
          <tspan
            x={e.x - 10}
            y={e.y + 14}
            style={{
              fontSize: 10,
            }}
          >
            {'min'}
          </tspan>
        )}
      </text>
    )
  }

  renderChart4Bar({ label: value, bar: { y, height, color, label = "" } }, renderLateralIndicator, closeLateralIndicator) {
    const { reports, translate } = this.props
    const [, exclusionReason] = label.split(' - ')
    const data = this.getDataChart4(reports)
    const index = data.indexOf(
      data.find(
        (chart) => chart.exclusionReason === exclusionReason
      )
    )

    return (
      <rect
        cursor='pointer'
        fill={color}
        width={28}
        height={height}
        x={15 + (28 + 55) * index}
        y={y}
        onClick={() => {
          this.setState((prevState) => ({
            ordersModal: {
              ...prevState.ordersModal,
              title: translate(
                'totem.query.log.sales.order.deleted.option'
              ),
              queueReportType: REPORT_TYPES.REASONS_FOR_DELETING_MONITOR_ORDERS,
              extraFilter: { exclusionReason },
              header: [],
              show: true,
              colorizeTime: true,
              data: this.catchReportsByExclusionReason(exclusionReason),
              showReasons: true,
            },
          }))
          closeLateralIndicator()
        }}
        onMouseEnter={(e) =>
          renderLateralIndicator(e, value, index)
        }
        onMouseLeave={closeLateralIndicator}
      />
    )
  }

  renderChart4AxisBottom(e) {
    const { reports, translate } = this.props
    const element = this.getDataChart4(reports)[e.tickIndex]
    const exclusionReason = element?.exclusionReason
    const openReasonModal = () => {
      if (!exclusionReason) return
      this.setState((prevState) => ({
        ordersModal: {
          ...prevState.ordersModal,
          title: translate(
            'totem.query.log.sales.order.deleted.option'
          ),
          queueReportType: REPORT_TYPES.REASONS_FOR_DELETING_MONITOR_ORDERS,
          extraFilter: { exclusionReason },
          header: [],
          show: true,
          colorizeTime: true,
          data: this.catchReportsByExclusionReason(exclusionReason),
          showReasons: true,
        },
      }))
    }

    return (
      <g
        style={{ cursor: exclusionReason ? 'pointer' : 'default' }}
        onClick={openReasonModal}
      >
        <text
          alignmentBaseline={e.textBaseline}
          textAnchor={e.textAnchor}
        >
          {wrapLabel(
            (exclusionReason || " - ").split(' '),
            18
          ).map((line, i) => (
            <tspan
              key={`${line}-${i}`}
              x={29 + 83 * e.tickIndex}
              y={15 + 10 * (i + 1)}
              style={{
                fontSize: 10,
              }}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    )
  }

  render() {
    const { translate, reports } = this.props

    const {
      showFilter,
      showSideFilter,
      ordersModal,
      detailsModal,
      sideFilterValues,
      showErrorModal,
    } = this.state

    const [below, above, excluded, neutral] = this.indicators
    const { orderQuantity, averageTime, orderBelow, orderAbove } = this.getReportStats(reports)

    return (
      <Fragment>
        <ContentHeader>{translate('totem.reports.title')}</ContentHeader>
        <Fragment>
          {showFilter ? (
            <div className='common-report'>
              <div className='content'>
                <div className='filters'>
                  <h2>{translate('commonReport.filters')}</h2>
                  <Form
                    fields={this.formFields}
                    onSubmit={this.handleFilter}
                    translate={translate}
                  />
                </div>
              </div>
            </div>
          ) : (
            <Fragment>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 'auto',
                    alignSelf: 'center',
                  }}
                >
                  <div className='button-filter-container'>
                    <button
                      className='custom-modal-confirm-button'
                      onClick={this.handleDownloadGeneral}
                      disabled={reports.length === 0}>
                      <DownloadIconSVG className="download-svg-small" />
                      <span style={{ color: '#fff' }}>{"Baixar relatório de detalhes"}</span>
                    </button>

                    <Button
                      className='btn btn-secondary'
                      text={translate('mobile.filter')}
                      onClick={this.handleShowSideFilter}

                      svgIcon
                      svgComponent={<FilterIconSVG className="margin-default-svg" newDesign={false} />}
                    />
                  </div>
                  <div className='widgets-container'>
                    <div
                      className='widgets-totem hovered value-green'
                      onClick={this.handleQuantityWidgetClick}
                    >
                      <div className='widget-title'>
                        <p> {translate('totem.reports.filter.header.quantity')} </p>
                      </div>

                      <div className='widget-value'>
                        <h3>{orderQuantity}</h3>
                      </div>
                    </div>

                    <div
                      className={`widgets-totem hovered ${moment(averageTime, FORMAT_HOUR) > TIME_LIMIT
                        ? 'value-red'
                        : 'value-green'
                        }`}
                      onClick={this.handleTimeOptionsWidgetClick}
                    >
                      <div className='widget-title'>
                        <p> {translate('totem.reports.time.options.title')} </p>
                      </div>
                      <div className='widget-value'>
                        <h3>{`${calculateMinutes(averageTime)} min`}</h3>
                      </div>
                    </div>

                    <div
                      className='widgets-totem hovered value-green'
                      onClick={this.handleBelowWidgetClick}
                    >
                      <div className='widget-title'>
                        <p> {translate('totem.reports.filter.header.under')} </p>
                      </div>
                      <div className='widget-value'>
                        <h3>{orderBelow}</h3>
                      </div>
                    </div>
                    <div
                      className='widgets-totem hovered value-red'
                      onClick={this.handleAboveWidgetClick}
                    >
                      <div className='widget-title'>
                        <p> {translate('totem.reports.filter.header.above')} </p>
                      </div>
                      <div className='widget-value'>
                        <h3>{orderAbove}</h3>
                      </div>
                    </div>
                  </div>

                  {showSideFilter && (
                    <SideFilterForm
                      translate={translate}
                      setSideFilterVisible={this.handleHideSideFilter}
                      handleFilter={this.handleFilter}
                      initialValues={sideFilterValues}
                    />
                  )}
                  {ordersModal.show && (
                    /* TODO: Implement virtualization in OrdersModal (BLM-15684) */
                    <OrdersModal
                      onDownload={this.getDownloadHandler(ordersModal.queueReportType, true)}
                      closeModal={this.handleCloseOrdersModal}
                      title={ordersModal.title}
                      header={ordersModal.header}
                      t={translate}
                      colorizeTime={ordersModal.colorizeTime}
                      showDetailModal={this.handleShowDetailModal}
                      data={ordersModal.data}
                      showReasons={ordersModal.showReasons}
                    />
                  )}
                  {detailsModal.show && (
                    <DetailsModal
                      t={translate}
                      closeModal={this.handleCloseDetailsModal}
                      turnBack={this.handleTurnBackFromDetailsModal}
                      orderDetail={detailsModal.orderDetails}
                    />
                  )}
                </div>
              </div>
              <Chart
                onDownload={this.handleDownloadOrdersByStore}
                t={translate}
                title={translate('totem.reports.filter.header.store')}
                indicators={[below, above, excluded]}
                data={this.getDataChart1(reports)}
                barSpace={35}
                keys={['below', 'above', 'excluded']}
                indexBy='store'
                indicate
                bar={this.renderChart1Bar}
                axisBottomRender={this.renderChart1AxisBottom}
                valuesY={calculateValuesY(
                  this.getDataChart1(reports).map(
                    ({ above, below, excluded }) => above + excluded + below
                  )
                )}
                axisLeftRender={this.renderChartAxisLeft}
              />
              <Chart
                onDownload={this.handleDownloadOrdersByDay}
                t={translate}
                title={translate('totem.reports.graph.quantity.title')}
                indicators={[neutral]}
                data={this.getDataChart2(reports)}
                barSpace={20}
                keys={['purchases']}
                indexBy='date'
                bar={this.renderChart2Bar}
                axisBottomRender={this.renderChart2AxisBottom}
                valuesY={calculateValuesY(
                  this.getDataChart2(reports).map(({ purchases }) => purchases)
                )}
                axisLeftRender={this.renderChartAxisLeft}
              />
              <Chart
                onDownload={this.handleDownloadAverageTime}
                t={translate}
                title={translate('totem.reports.graph.time.title')}
                indicators={[below, above]}
                data={this.getDataChart3(reports)}
                barSpace={35}
                keys={['average']}
                indexBy='date'
                indicate
                bar={this.renderChart3Bar}
                axisBottomRender={this.renderChart3AxisBottom}
                valuesY={calculateValuesY(
                  this.getDataChart3(reports).map(({ average }) => average),
                  true
                )}
                axisLeftRender={this.renderChart3AxisLeft}
              />
              <Chart
                onDownload={this.handleDownloadReasons}
                t={translate}
                title={translate('totem.reports.filter.header.excluded')}
                indicators={[neutral]}
                data={this.getDataChart4(reports)}
                barSpace={55}
                keys={['purchases']}
                indexBy='exclusionReason'
                bar={this.renderChart4Bar}
                axisBottomRender={this.renderChart4AxisBottom}
                valuesY={calculateValuesY(
                  this.getDataChart4(reports).map(({ purchases }) => purchases)
                )}
                axisLeftRender={this.renderChartAxisLeft}
              />
            </Fragment>
          )}
        </Fragment>

        {showErrorModal && this.renderModalError()}
      </Fragment>
    )
  }
}

const mapStateToProps = ({ totemReportsNewReducer }) => ({
  reports: totemReportsNewReducer.records,
  error: totemReportsNewReducer.error,
  isFetching: totemReportsNewReducer.isFetching,
})

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators({ ...totemReportsActions }, dispatch),
  dispatch,
})

const TotemReportsContainerTranslated = translate(null, { wait: true })(
  TotemReportsContainer
)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TotemReportsContainerTranslated)

export { TotemReportsContainer }


